import React, { useState } from "react";
import { Layout, Menu, Typography } from "antd";
import { useMenu, useNavigation, useResource } from "@refinedev/core";
import { Link, useLocation } from "react-router-dom";
import {
    DashboardOutlined,
    DollarOutlined,
    TagOutlined,
    BankOutlined,
    TeamOutlined,
    AppstoreOutlined,
    ShoppingOutlined,
    ShopOutlined,
    BarChartOutlined,
    FileTextOutlined,
    CalculatorOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    DownOutlined,
    RightOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";

const { Sider } = Layout;
const { Text } = Typography;

// Icon mapping for resources
const iconMap: Record<string, React.ReactNode> = {
    dashboard: <DashboardOutlined />,
    currency: <DollarOutlined />,
    taxes: <TagOutlined />,
    company: <BankOutlined />,
    customers: <TeamOutlined />,
    vendors: <TeamOutlined />,
    Product: <AppstoreOutlined />,
    stock: <AppstoreOutlined />,
    "stock-alerts": <AppstoreOutlined />,
    inventory: <AppstoreOutlined />,
    purchase: <ShoppingOutlined />,
    purchaseorders: <ShoppingOutlined />,
    purchases: <ShoppingOutlined />,
    "sales-management": <ShopOutlined />,
    sales: <ShopOutlined />,
    saleorders: <ShopOutlined />,
    salereturns: <ShopOutlined />,
    quotations: <ShopOutlined />,
    users: <TeamOutlined />,
    accounting: <CalculatorOutlined />,
    chartofaccounts: <BankOutlined />,
    journalentries: <FileTextOutlined />,
    "accounting-periods": <CalculatorOutlined />,
    "period-balances": <BarChartOutlined />,
    "financial-reports": <BarChartOutlined />,
    "vat-reports": <BarChartOutlined />,
    reports: <BarChartOutlined />,
    prices: <TagOutlined />,
    fiscalisation: <BankOutlined />,
    logout: <LogoutOutlined />,
};

export const CustomSider: React.FC = () => {
    const [collapsed, setCollapsed] = useState(false);
    const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
    const location = useLocation();

    const renderMenuItems = (items: typeof menuItems): MenuProps["items"] => {
        return items
            .filter((item) => !item.meta?.hide)
            .map((item) => {
                const icon = iconMap[item.name] || item.icon;
                
                if (item.children && item.children.length > 0) {
                    const visibleChildren = item.children.filter(child => !child.meta?.hide);
                    if (visibleChildren.length === 0) return null;
                    
                    return {
                        key: item.key || item.name,
                        icon: icon,
                        label: item.label,
                        children: renderMenuItems(visibleChildren),
                    };
                }

                return {
                    key: item.key || item.name,
                    icon: icon,
                    label: item.route ? (
                        <Link to={item.route}>{item.label}</Link>
                    ) : (
                        item.label
                    ),
                };
            })
            .filter(Boolean);
    };

    return (
        <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            trigger={null}
            width={260}
            collapsedWidth={80}
            className="custom-sider"
            theme="light"
        >
            {/* Logo Area */}
            <div className="sider-logo">
                <div className="logo-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                        <rect x="3" y="3" width="7" height="7" rx="2" fill="#3b82f6" />
                        <rect x="14" y="3" width="7" height="7" rx="2" fill="#60a5fa" />
                        <rect x="3" y="14" width="7" height="7" rx="2" fill="#60a5fa" />
                        <rect x="14" y="14" width="7" height="7" rx="2" fill="#93c5fd" />
                    </svg>
                </div>
                {!collapsed && (
                    <Text strong className="logo-text">FrontDesk</Text>
                )}
            </div>

            {/* Collapse Toggle */}
            <div 
                className="collapse-trigger"
                onClick={() => setCollapsed(!collapsed)}
            >
                {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </div>

            {/* Navigation Menu */}
            <Menu
                mode="inline"
                selectedKeys={[selectedKey]}
                defaultOpenKeys={defaultOpenKeys}
                items={renderMenuItems(menuItems)}
                className="custom-menu"
            />
        </Sider>
    );
};
